import type { JSX } from 'react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { DOMAIN_META, STATUS_BADGE } from '../../config/activity-display';
import { formatRelativeTime } from '../../lib/activity-time';
import type { ActivityItem } from '../../model/activity';
import AlertActivityContent from './AlertActivityContent';
import QuestionActivityContent from './QuestionActivityContent';
import TaskActivityContent from './TaskActivityContent';

interface ActivityRowProps {
  item: ActivityItem;
}

const ActivityRow = ({ item }: ActivityRowProps): JSX.Element => {
  const [copied, setCopied] = useState(false);
  const meta = DOMAIN_META[item.domain];
  const DomainIcon = meta.icon;
  const statusBadge = STATUS_BADGE[item.status] ?? {
    bg: 'bg-neutral-100 text-neutral-600',
    label: item.status,
  };
  const isResolved = item.status === 'archived' || item.status === 'done' || item.status === 'answered';

  const handleCopyId = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(String(item.clientId));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <li
      className={cn(
        'group flex gap-3 rounded-lg border border-l-[3px] border-border-subtle bg-bg-surface px-4 py-3 shadow-xs',
        'transition-colors hover:bg-bg-surface-raised',
        meta.rowBorderColor,
        isResolved && 'opacity-75'
      )}
    >
      <div className="mt-0.5 shrink-0">
        <DomainIcon className={cn('size-5', meta.rowIconColor)} aria-hidden />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="m-0 text-sm font-semibold text-text-default font-body">
            {item.clientName || 'Cliente sin nombre'}
            <button
              type="button"
              onClick={handleCopyId}
              className="ml-1.5 inline-flex cursor-copy items-center rounded px-1 py-0.5 text-xs font-normal text-text-muted transition-colors hover:bg-bg-surface-raised hover:text-text-default font-body"
              title="Copiar ID del cliente"
            >
              {copied ? '¡Copiado!' : `ID ${item.clientId}`}
            </button>
            {item.subProjectName ? (
              <span className="ml-1.5 text-xs font-normal text-text-muted">· {item.subProjectName}</span>
            ) : null}
            <span className="sr-only">{meta.shortLabel}</span>
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                statusBadge.bg
              )}
            >
              {!isResolved ? <span className="inline-block size-1.5 rounded-full bg-current" aria-hidden /> : null}
              {statusBadge.label}
            </span>
            <span
              className="whitespace-nowrap text-xs text-text-subtle font-body"
              title={new Date(item.createdAt).toLocaleString('es-CL')}
            >
              {formatRelativeTime(item.createdAt)}
            </span>
          </div>
        </div>

        {item.domain === 'alert' ? <AlertActivityContent item={item} /> : null}
        {item.domain === 'task' ? <TaskActivityContent item={item} /> : null}
        {item.domain === 'question' ? <QuestionActivityContent item={item} /> : null}
      </div>
    </li>
  );
};

export default ActivityRow;
